/**
 * Máscaras pt-BR para entrada de dados.
 *
 * Estratégia: a UI mostra o valor mascarado; a regra de negócio (Zod + payload
 * pra API) trabalha sempre com dígitos puros. Cada `apply*Mask` recebe o input
 * cru do usuário e devolve a versão visual; `digitsOnly` extrai o canônico.
 */

export function digitsOnly(value: string | null | undefined): string {
  return (value ?? '').replace(/\D+/g, '');
}

export function applyCpfMask(raw: string): string {
  const digits = digitsOnly(raw).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function applyCnpjMask(raw: string): string {
  const digits = digitsOnly(raw).slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** CPF ou CNPJ, automático pelo número de dígitos. */
export function applyCpfCnpjMask(raw: string): string {
  const digits = digitsOnly(raw);
  return digits.length <= 11 ? applyCpfMask(raw) : applyCnpjMask(raw);
}

export function applyCepMask(raw: string): string {
  const digits = digitsOnly(raw).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function applyPhoneMask(raw: string): string {
  const digits = digitsOnly(raw).slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Converte o input do usuário em moeda BRL.
 * O número é interpretado em centavos (cada dígito empurra pra esquerda).
 * Retorna { cents, formatted }; cents é o canônico pra cálculos e schema.
 */
export interface BrlInputResult {
  cents: number;
  formatted: string;
  numericValue: number;
}

export function parseBrlInput(raw: string): BrlInputResult {
  const digits = digitsOnly(raw);
  if (digits.length === 0) {
    return { cents: 0, formatted: '', numericValue: 0 };
  }
  const cents = Number.parseInt(digits, 10);
  const numericValue = cents / 100;
  return {
    cents,
    formatted: BRL_FORMATTER.format(numericValue),
    numericValue,
  };
}

export function formatBrlNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return BRL_FORMATTER.format(value);
}

const CPF_CNPJ_REGEX = {
  cpf: /^\d{11}$/,
  cnpj: /^\d{14}$/,
};

export function isValidCpfCnpjLength(value: string | null | undefined): boolean {
  const digits = digitsOnly(value);
  return CPF_CNPJ_REGEX.cpf.test(digits) || CPF_CNPJ_REGEX.cnpj.test(digits);
}

export function isValidCepLength(value: string | null | undefined): boolean {
  return digitsOnly(value).length === 8;
}
